/**
 * Màn So sánh model — trả lời câu Lynust đặt từ đầu: *"model nào viết tốt hơn cho dự án này"*.
 *
 * Ba phần, và **thứ tự trên màn là thứ tự phải làm**:
 *
 * 1. **Ước tiền TRƯỚC nút chạy.** Đây là nút đắt nhất dashboard — một lần bấm sinh
 *    `số đề × số model` lời gọi. Con số phải nằm cạnh nút, không nằm trong tài liệu.
 * 2. **Chấm mù.** Bài hiện ra **không kèm tên model, không kèm điểm máy**. Biết trước bài nào của
 *    Opus thì điểm sẽ thiên vị — đây không phải hình thức.
 * 3. **Bảng xếp hạng.** Đọc sau khi chấm, không phải trước.
 *
 * ⚠️ Nội dung bài hiện **HTML thô**, cố ý — cùng lý do với màn Chi tiết bài: render đẹp thì
 * Markdown lọt trông như chữ thường và **không ai thấy** (`AP-88`).
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api.ts";
import {
  Bao,
  Chip,
  DangTai,
  DauThe,
  DauTrang,
  Nut,
  Rong,
  The,
  Truong,
  lopO,
} from "../thanh-phan/giao-dien.tsx";
import { useToast } from "../thanh-phan/toast.tsx";

const BO_DE_MAC_DINH = "bo-de-2026-09";

/** Ba trục chấm, mỗi trục 1–5. Đúng ba câu hỏi trong `model-eval.md`. */
const TRUC = [
  { khoa: "tuNhien", nhan: "Đọc có tự nhiên không" },
  { khoa: "chuyenMon", nhan: "Có đúng chuyên môn thiết bị y tế không" },
  { khoa: "dungDuocNgay", nhan: "Dùng được luôn hay phải sửa" },
] as const;

export function SoSanhModel() {
  const qc = useQueryClient();
  const toast = useToast();
  const [boDe, datBoDe] = useState(BO_DE_MAC_DINH);
  const [luot, datLuot] = useState("luot-1");
  const [chon, datChon] = useState<string[]>(["claude-sonnet-5"]);

  const { data: models } = useQuery({ queryKey: ["eval-models"], queryFn: api.evalModels });
  const { data: uoc } = useQuery({
    queryKey: ["eval-uoc", boDe, chon.join(",")],
    queryFn: () => api.evalUocTien(boDe, chon),
    enabled: chon.length > 0,
  });
  const { data: bang } = useQuery({
    queryKey: ["eval-bang", boDe, luot],
    queryFn: () => api.evalBangXepHang(boDe, luot),
  });
  const { data: deCham, isPending: dangTaiCham } = useQuery({
    queryKey: ["eval-cham-mu", boDe, luot],
    queryFn: () => api.evalChamMu(boDe, luot),
  });

  const chay = useMutation({
    mutationFn: () => api.evalChay(boDe, luot, chon),
    onSuccess: (kq) => {
      // Số tiền đứng trong toast, không chỉ trong bảng bên dưới: eval là lời gọi **tốn tiền thật**
      // duy nhất mà Lynust chủ động bấm, nên chi phí của lượt vừa rồi phải nói ra ngay tại chỗ.
      toast.on(
        `Đã chạy ${kq.daChay} lời gọi — $${kq.tienUsd.toFixed(4)}`,
        `${kq.thanhCong}/${kq.daChay} thành công.`,
      );
      void qc.invalidateQueries({ queryKey: ["eval-bang", boDe, luot] });
      void qc.invalidateQueries({ queryKey: ["eval-cham-mu", boDe, luot] });
    },
    onError: (loi: Error) => toast.loi("Eval không chạy được", loi.message),
  });

  return (
    <div className="min-w-0 space-y-5">
      <DauTrang
        tieuDe="So sánh model"
        moTa="Cùng một bộ đề đóng băng, mọi model nhận cùng system prompt, cùng user prompt, cùng ngân sách output."
      />

      {/* ── 1. Chạy ─────────────────────────────────────────────────── */}
      <The>
        <DauThe
          tieuDe="Chạy một lượt"
          phu="Bộ đề đóng băng — model mới ra thì chạy lại đúng bộ này để so được qua thời gian"
        />

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Truong nhan="Bộ đề">
            <input value={boDe} onChange={(e) => datBoDe(e.target.value)} className={lopO} />
          </Truong>
          <Truong nhan="Tên lượt" goiY="Chạy lại cùng lượt thì bỏ qua bài đã có — không tốn thêm tiền">
            <input value={luot} onChange={(e) => datLuot(e.target.value)} className={lopO} />
          </Truong>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {(models ?? []).map((m) => {
            const dangChon = chon.includes(m.catalogKey);
            return (
              <button
                key={m.catalogKey}
                type="button"
                title={m.moTa}
                onClick={() =>
                  datChon((cu) =>
                    dangChon ? cu.filter((x) => x !== m.catalogKey) : [...cu, m.catalogKey],
                  )
                }
                className={
                  dangChon
                    ? "rounded-btn bg-canh-500/12 px-2 py-1 text-2xs font-semibold text-canh-600 ring-1 ring-canh-200"
                    : "rounded-btn bg-muc-0 px-2 py-1 text-2xs text-muc-600 ring-1 ring-muc-300 hover:bg-muc-100"
                }
              >
                {m.catalogKey}
                <span className="ml-1 opacity-60">
                  ${m.giaVaoUsdMoiTrieu}/${m.giaRaUsdMoiTrieu}
                </span>
              </button>
            );
          })}
        </div>

        {/* ⭐ Ước tiền nằm CẠNH nút, không nằm trong tài liệu. */}
        {uoc !== undefined && (
          <div className="mt-4 rounded-tile bg-muc-100 p-3">
            <div className="so-thang-cot text-xl font-semibold text-muc-900">
              ~${uoc.uocUsd.toFixed(2)}
            </div>
            <p className="mt-1 text-2xs text-muc-600">
              {uoc.soDe} đề × {chon.length} model = <strong>{uoc.soLoiGoi} lời gọi</strong>. Đây là
              cấp số nhân duy nhất trong cả dự án — đọc con số này trước khi bấm.
            </p>
            <ul className="mt-2 space-y-0.5">
              {uoc.theoModel.map((m) => (
                <li key={m.catalogKey} className="text-2xs text-muc-600">
                  {m.catalogKey}: ~${m.uocUsd.toFixed(2)}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4">
          <Nut
            kieu="chinh"
            dangChay={chay.isPending}
            disabled={chon.length === 0}
            onClick={() => chay.mutate()}
          >
            Chạy eval
          </Nut>
        </div>

        {/* Kết quả và chi phí lượt eval đi bằng toast — xem `onSuccess`. */}
      </The>

      {/* ── 2. Chấm mù ──────────────────────────────────────────────── */}
      <The>
        <DauThe
          tieuDe="Chấm mù"
          phu="Không hiện tên model, không hiện điểm máy — biết trước thì điểm sẽ thiên vị"
        />
        {dangTaiCham ? (
          <DangTai />
        ) : (deCham ?? []).length === 0 ? (
          <Rong tieuDe="Chưa có bài nào chờ chấm">
            Chạy một lượt trước, hoặc mọi bài của lượt này đã được chấm xong.
          </Rong>
        ) : (
          <div className="mt-3 space-y-4">
            {(deCham ?? []).map((b, i) => (
              <MotBaiChamMu key={b.id} bai={b} thuTu={i + 1} tong={(deCham ?? []).length} />
            ))}
          </div>
        )}
      </The>

      {/* ── 3. Bảng xếp hạng ────────────────────────────────────────── */}
      <The>
        <DauThe tieuDe="Bảng xếp hạng" phu="Đọc SAU khi chấm, không phải trước" />
        {(bang ?? []).length === 0 ? (
          <Rong tieuDe="Chưa có kết quả">Lượt này chưa chạy.</Rong>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[640px] text-xs">
              <thead>
                <tr className="border-b border-muc-300 text-left text-muc-600">
                  <th className="py-2 pr-3 font-medium">Model</th>
                  <th className="py-2 pr-3 font-medium">Bài</th>
                  <th className="py-2 pr-3 font-medium">Điểm máy</th>
                  <th className="py-2 pr-3 font-medium">Tự nhiên</th>
                  <th className="py-2 pr-3 font-medium">Chuyên môn</th>
                  <th className="py-2 pr-3 font-medium">Dùng ngay</th>
                  <th className="py-2 pr-3 font-medium">Tiền</th>
                </tr>
              </thead>
              <tbody>
                {(bang ?? []).map((r) => (
                  <tr key={r._id} className="border-b border-muc-200">
                    <td className="py-2 pr-3 font-medium text-muc-900">{r._id}</td>
                    <td className="so-thang-cot py-2 pr-3">
                      {r.soThanhCong}/{r.soBai}
                    </td>
                    <td className="so-thang-cot py-2 pr-3">{r.diemMayTb?.toFixed(1) ?? "—"}</td>
                    {/* `null` = CHƯA CHẤM, khác hẳn 0 = "đã chấm, tệ". Hiện dấu gạch, không hiện 0. */}
                    <td className="so-thang-cot py-2 pr-3">
                      {r.soDaChamNguoi > 0 ? r.diemTuNhienTb?.toFixed(1) : "chưa chấm"}
                    </td>
                    <td className="so-thang-cot py-2 pr-3">
                      {r.soDaChamNguoi > 0 ? r.diemChuyenMonTb?.toFixed(1) : "chưa chấm"}
                    </td>
                    <td className="so-thang-cot py-2 pr-3">
                      {r.soDaChamNguoi > 0 ? r.diemDungDuocTb?.toFixed(1) : "chưa chấm"}
                    </td>
                    <td className="so-thang-cot py-2 pr-3">${r.tienUsd.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-3 text-2xs text-muc-500">
          <strong>Điểm máy cao không có nghĩa là bài hay</strong> — nó chỉ có nghĩa là bài không vi
          phạm thứ đo được. Một bài nhạt, đủ từ khoá, không mùi AI vẫn đạt tuyệt đối. Đó là lý do
          cột chấm người tồn tại và không được bỏ.
        </p>
      </The>
    </div>
  );
}

function MotBaiChamMu({
  bai,
  thuTu,
  tong,
}: {
  bai: { id: string; siteSlug: string; title: { vi?: string }; contentHtml: { vi?: string }; captionFacebook: string | null };
  thuTu: number;
  tong: number;
}) {
  const qc = useQueryClient();
  const [diem, datDiem] = useState<Record<string, number>>({});
  const [nhanXet, datNhanXet] = useState("");
  const [moRong, datMoRong] = useState(false);

  const cham = useMutation({
    mutationFn: () =>
      api.evalCham(bai.id, {
        tuNhien: diem.tuNhien ?? 0,
        chuyenMon: diem.chuyenMon ?? 0,
        dungDuocNgay: diem.dungDuocNgay ?? 0,
        nhanXet: nhanXet.trim() || undefined,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["eval-cham-mu"] }),
  });

  const du = TRUC.every((t) => (diem[t.khoa] ?? 0) >= 1);

  return (
    <div className="min-w-0 rounded-tile border border-muc-300 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Chip muc="thuong">
          {thuTu}/{tong}
        </Chip>
        <Chip muc="thuong">{bai.siteSlug}</Chip>
        <span className="min-w-0 break-words text-sm font-medium text-muc-900">
          {bai.title?.vi ?? "(không có tiêu đề)"}
        </span>
      </div>

      <button
        type="button"
        onClick={() => datMoRong((x) => !x)}
        className="mt-2 text-2xs text-muc-600 underline hover:text-muc-900"
      >
        {moRong ? "Thu gọn" : "Đọc nội dung"}
      </button>

      {moRong && (
        <pre className="mt-2 max-h-96 overflow-auto rounded-tile bg-muc-100 p-3 text-2xs leading-relaxed text-muc-700">
          {bai.contentHtml?.vi ?? ""}
        </pre>
      )}

      {bai.captionFacebook && (
        <p className="mt-2 break-words text-2xs text-muc-600">FB: {bai.captionFacebook}</p>
      )}

      <div className="mt-3 space-y-2">
        {TRUC.map((t) => (
          <div key={t.khoa} className="flex flex-wrap items-center gap-2">
            <span className="min-w-[220px] text-2xs text-muc-600">{t.nhan}</span>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => datDiem((cu) => ({ ...cu, [t.khoa]: n }))}
                className={
                  diem[t.khoa] === n
                    ? "rounded-btn bg-muc-900 px-2 py-0.5 text-2xs text-muc-0"
                    : "rounded-btn bg-muc-0 px-2 py-0.5 text-2xs text-muc-600 ring-1 ring-muc-300 hover:bg-muc-100"
                }
              >
                {n}
              </button>
            ))}
          </div>
        ))}
      </div>

      <div className="mt-3">
        <input
          value={nhanXet}
          onChange={(e) => datNhanXet(e.target.value)}
          placeholder="Nhận xét (tuỳ chọn)"
          className={lopO}
        />
      </div>

      <div className="mt-3">
        <Nut kieu="chinh" disabled={!du} dangChay={cham.isPending} onClick={() => cham.mutate()}>
          Lưu điểm
        </Nut>
        {cham.error && <span className="ml-2 text-2xs text-loi-600">{cham.error.message}</span>}
      </div>
    </div>
  );
}
